import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_VAPID_KEY,
} from "./config";

const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
  storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let firebaseApp;
try {
  if (!firebase.apps.length) {
    firebaseApp = firebase.initializeApp(firebaseConfig);
  } else {
    firebaseApp = firebase.app();
  }
} catch {
  firebaseApp = null;
}

let messaging: firebase.messaging.Messaging | null = null;
let messagingInitializationPromise: Promise<firebase.messaging.Messaging | null> | null =
  null;

const initializeMessaging =
  async (): Promise<firebase.messaging.Messaging | null> => {
    if (messaging) return messaging;
    if (messagingInitializationPromise) return messagingInitializationPromise;

    messagingInitializationPromise = (async () => {
      try {
        const hasServiceWorker = "serviceWorker" in navigator;
        const hasNotification = "Notification" in window;
        const isSecureContext =
          window.isSecureContext ||
          location.protocol === "https:" ||
          location.hostname === "localhost" ||
          location.hostname === "127.0.0.1";

        if (!hasServiceWorker || !hasNotification || !isSecureContext) {
          return null;
        }

        if (!firebaseApp) {
          try {
            if (!firebase.apps.length) {
              firebaseApp = firebase.initializeApp(firebaseConfig);
            } else {
              firebaseApp = firebase.app();
            }
          } catch {
            return null;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 0));
        messaging = firebase.messaging();
        return messaging;
      } catch {
        return null;
      } finally {
        messagingInitializationPromise = null;
      }
    })();

    return messagingInitializationPromise;
  };

export const getMessagingInstance = async () => {
  if (!messaging) {
    messaging = await initializeMessaging();
  }
  return messaging;
};

export const setupFcmMessageHandler = async () => {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) return;

  try {
    messagingInstance.onMessage(async (payload) => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const notification =
            payload.notification || (payload as any).data || {};
          const title =
            notification.title ||
            (payload as any).data?.title ||
            "Notification";
          const body = notification.body || (payload as any).data?.body || "";
          const icon = notification.icon || "/logo.svg";

          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            registration.showNotification(title, {
              body,
              icon,
              data: (payload as any).data || {},
            });
          }
        }
      } catch {}
    });
  } catch {}
};

export const getFCMToken = async () => {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) return null;

  try {
    let permission = Notification.permission;
    if (permission === "default") {
      try {
        permission = await Notification.requestPermission();
      } catch {}
    }

    if (permission !== "granted") return null;

    if ("serviceWorker" in navigator) {
      let registration;
      try {
        registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
          { scope: "/" },
        );
      } catch {
        return null;
      }

      try {
        await navigator.serviceWorker.ready;
      } catch {
        return null;
      }

      try {
        const currentToken = await messagingInstance.getToken({
          vapidKey: VITE_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        return currentToken || null;
      } catch {
        return null;
      }
    } else {
      return null;
    }
  } catch {
    return null;
  }
};

export const requestNotificationPermissionAndGetToken = async (): Promise<
  string | null
> => {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/" },
      );
      await navigator.serviceWorker.ready;
      const currentToken = await messagingInstance.getToken({
        vapidKey: VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      return currentToken || null;
    } else {
      return null;
    }
  } catch {
    return null;
  }
};
